import { create } from 'zustand';
import type { PosSyncMode } from '@/offline/db';
import { resolveEffectivePosSyncMode } from '@/offline/syncModePolicy';

interface SyncProgress {
  total: number;
  done: number;
  failed: number;
  paused: boolean;
}

export type CatalogSyncPhase =
  | 'preparing'
  | 'meta'
  | 'items'
  | 'customers'
  | 'suppliers'
  | 'expenses'
  | 'sale_invoices'
  | 'purchase_invoices'
  | 'finalizing'
  | 'done'
  | 'error';

export interface CatalogSyncProgress {
  open: boolean;
  phase: CatalogSyncPhase;
  label: string;
  entityDone: number;
  entityTotal: number;
  overallDone: number;
  overallTotal: number;
  error?: string | null;
}

export function getDefaultCatalogProgress(): CatalogSyncProgress {
  return {
    open: false,
    phase: 'preparing',
    label: '',
    entityDone: 0,
    entityTotal: 0,
    overallDone: 0,
    overallTotal: 0,
    error: null,
  };
}

interface SyncStoreState {
  posSyncMode: PosSyncMode;
  isOnline: boolean;
  isServerReachable: boolean;
  catalogSyncedAt: string | null;
  pendingQueueCount: number;
  failedQueueCount: number;
  isSyncing: boolean;
  syncProgress: SyncProgress | null;
  catalogSyncProgress: CatalogSyncProgress;
  lastSyncError: string | null;
  setPosSyncMode: (mode: PosSyncMode) => void;
  setOnline: (v: boolean) => void;
  setServerReachable: (v: boolean) => void;
  setCatalogSyncedAt: (v: string | null) => void;
  setQueueCounts: (pending: number, failed: number) => void;
  setIsSyncing: (v: boolean) => void;
  setSyncProgress: (p: SyncProgress | null) => void;
  setCatalogSyncProgress: (p: CatalogSyncProgress) => void;
  setLastSyncError: (e: string | null) => void;
  isLocalFirstMode: () => boolean;
}

export const useSyncStore = create<SyncStoreState>((set, get) => ({
  // Desktop overrides to Local First via resolveEffectivePosSyncMode on set.
  posSyncMode: resolveEffectivePosSyncMode('Live Online'),
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  // Start unreachable until first ping succeeds — avoids treating a dead link as online.
  isServerReachable: false,
  catalogSyncedAt: null,
  pendingQueueCount: 0,
  failedQueueCount: 0,
  isSyncing: false,
  syncProgress: null,
  catalogSyncProgress: getDefaultCatalogProgress(),
  lastSyncError: null,
  setPosSyncMode: (mode) => set({ posSyncMode: resolveEffectivePosSyncMode(mode) }),
  setOnline: (v) => set({ isOnline: v }),
  setServerReachable: (v) => set({ isServerReachable: v }),
  setCatalogSyncedAt: (v) => set({ catalogSyncedAt: v }),
  setQueueCounts: (pending, failed) => set({ pendingQueueCount: pending, failedQueueCount: failed }),
  setIsSyncing: (v) => set({ isSyncing: v }),
  setSyncProgress: (p) => set({ syncProgress: p }),
  setCatalogSyncProgress: (p) => set({ catalogSyncProgress: p }),
  setLastSyncError: (e) => set({ lastSyncError: e }),
  isLocalFirstMode: () => {
    const m = get().posSyncMode;
    return m === 'Local First Auto Sync' || m === 'Offline Always Manual Sync';
  },
}));
